package org.gymly.util

import kotlinx.browser.window
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlin.coroutines.resume
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi
import kotlin.js.ExperimentalJsExport
import kotlin.js.JsExport

actual fun openImagePicker(onResult: (PickedImage?) -> Unit) {
    BrowserFileBridge.open(accept = "image/*", onResult = onResult)
}

actual fun openProofFilePicker(onResult: (PickedImage?) -> Unit) {
    BrowserFileBridge.open(accept = "image/*,application/pdf,.pdf", onResult = onResult)
}

actual fun openPromoMediaPicker(onResult: (PickedImage?) -> Unit) {
    BrowserFileBridge.open(accept = "image/*,video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov", onResult = onResult)
}

actual suspend fun pickImage(): PickedImage? = suspendCancellableCoroutine { cont ->
    openImagePicker { result ->
        if (cont.isActive) cont.resume(result)
    }
}

actual suspend fun pickProofFile(): PickedImage? = suspendCancellableCoroutine { cont ->
    openProofFilePicker { result ->
        if (cont.isActive) cont.resume(result)
    }
}

@OptIn(ExperimentalEncodingApi::class, ExperimentalJsExport::class)
private object BrowserFileBridge {
    private var pending: ((PickedImage?) -> Unit)? = null

    init {
        // Called from plain JS when the user finishes choosing a file.
        window.asDynamic().__gymlyFilePicked = { dataUrl: String?, fileName: String, mime: String ->
            deliver(dataUrl, fileName, mime)
        }
    }

    fun open(accept: String, onResult: (PickedImage?) -> Unit) {
        // Replace previous waiter without notifying cancel — avoids wiping Compose state mid-pick.
        pending = onResult
        window.asDynamic().__gymlyFilePicked = { dataUrl: String?, fileName: String, mime: String ->
            deliver(dataUrl, fileName, mime)
        }
        // Pass accept via window — Kotlin/JS does not inject params into block-body js("...") snippets.
        window.asDynamic().__gymlyFileAccept = accept
        jsOpenFilePicker()
    }

    private fun deliver(dataUrl: String?, fileName: String, mime: String) {
        val cb = pending ?: return
        pending = null
        if (dataUrl.isNullOrBlank() || !dataUrl.contains(',')) {
            cb(null)
            return
        }
        try {
            val bytes = Base64.decode(dataUrl.substringAfter(','))
            if (bytes.isEmpty()) {
                cb(null)
                return
            }
            val resolvedMime = mime.takeIf { it.isNotBlank() } ?: guessMime(fileName)
            cb(PickedImage(bytes = bytes, fileName = fileName.ifBlank { "file" }, mimeType = resolvedMime))
        } catch (_: Throwable) {
            cb(null)
        }
    }
}

/**
 * Pure browser file picker — avoids fragile Kotlin/DOM event bindings inside Compose dialogs.
 * Must stay synchronous from the click handler so the browser allows input.click().
 *
 * Reads accept from window.__gymlyFileAccept (set by [BrowserFileBridge.open]) so this
 * expression-body [js] snippet does not depend on Kotlin parameter name injection.
 */
private fun jsOpenFilePicker(): Unit =
    js(
        """{
          var accept = window.__gymlyFileAccept || 'image/*';
          var input = document.getElementById('__gymly_file_input');
          if (!input) {
            input = document.createElement('input');
            input.id = '__gymly_file_input';
            input.type = 'file';
            input.style.cssText = 'position:fixed;left:-10000px;top:0;width:1px;height:1px;opacity:0;';
            document.body.appendChild(input);
            input.addEventListener('change', function () {
              var file = input.files && input.files[0];
              if (!file) {
                if (window.__gymlyFilePicked) window.__gymlyFilePicked(null, '', '');
                return;
              }
              var reader = new FileReader();
              reader.onload = function () {
                if (window.__gymlyFilePicked) {
                  window.__gymlyFilePicked(String(reader.result || ''), file.name || 'file', file.type || '');
                }
              };
              reader.onerror = function () {
                if (window.__gymlyFilePicked) window.__gymlyFilePicked(null, '', '');
              };
              reader.readAsDataURL(file);
            });
            input.addEventListener('cancel', function () {
              if (window.__gymlyFilePicked) window.__gymlyFilePicked(null, '', '');
            });
          }
          input.accept = accept;
          input.value = '';
          input.click();
        }""",
    )

private fun guessMime(fileName: String): String {
    val lower = fileName.lowercase()
    return when {
        lower.endsWith(".png") -> "image/png"
        lower.endsWith(".gif") -> "image/gif"
        lower.endsWith(".webp") -> "image/webp"
        lower.endsWith(".pdf") -> "application/pdf"
        lower.endsWith(".mp4") -> "video/mp4"
        lower.endsWith(".webm") -> "video/webm"
        lower.endsWith(".mov") -> "video/quicktime"
        lower.endsWith(".jpg") || lower.endsWith(".jpeg") -> "image/jpeg"
        else -> "application/octet-stream"
    }
}

// Keep a reference so DCE doesn't strip the bridge object.
@Suppress("unused")
@JsExport
fun gymlyFilePickerPing(): String = "ok"
